import { getUnixTime, format } from 'date-fns';
import { defaultCurrencyId } from '@/finsight/ui/components/portfolioMonitor/constants';
import { IPortfolioItem } from '@/finsight/domain/vo/PortfolioMonitor/IPortfoliosCollection';
import { ICreatePortfolioFormState } from '@/finsight/domain/vo/PortfolioMonitor/ICreatePortfolioFormState';
import { ICreatePortfolioPayload } from '@/finsight/domain/vo/PortfolioMonitor/ICreatePortfolioPayload';
import { mapCurrencyToOption } from '@/evercall/ui/common/pricing/helpers';
import { ICurrency } from '@/dealroadshow/domain/vo/ICurrency';
import { v4 as uuid } from 'uuid';
import { removeDelimiters } from '@/Framework/dataHelpers/convertTypes';
import { TIMEZONE_DEFAULT, YEAR_MONTH_DAY_SLASH_FORMAT } from '@/Framework/DateTime/dateFormats';
import { deprecatedParseLegacyDateString } from '@/Framework/DateTime/parseDateString';

export const normalizeCreatePortfolioPayload = (payload: ICreatePortfolioFormState): ICreatePortfolioPayload => ({
  ...payload,
  lists: payload.lists.map((list, index) => ({
    ...list,
    priority: list?.priority || index + 1,
    positions: list?.positions?.length
      ? list.positions.map(({
        datePurchased,
        positionSize,
        dollarPricePaid,
        currencyId,
        id,
        name,
        cusip,
        bond,
      }) => ({
        id,
        cusip,
        name: typeof name === 'string' ? name : (name as { label: string })?.label || null,
        datePurchased: datePurchased ? getUnixTime(new Date(datePurchased)) : null,
        positionSize: Number(removeDelimiters(positionSize)) || null,
        dollarPricePaid: Number(dollarPricePaid) || null,
        currencyId: typeof currencyId === 'string' ? currencyId : (currencyId?.value || defaultCurrencyId),
        bond: {
          dealId: bond.dealId,
          trancheId: bond.trancheId,
          customBondName: bond.customBondName === '' ? null : bond.customBondName?.slice(0, 50) || null,
        },
    })) : [],
  })),
});

export const normalizePortfolioToFormState = (
    portfolio: IPortfolioItem, currencies: ICurrency[] = [],
): ICreatePortfolioFormState => ({
  ...portfolio,
  lists: portfolio?.lists.length ? portfolio?.lists.map(({ positions = [], ...list }) => ({
    ...list,
    positions: positions.map(({
      positionSize,
      dollarPricePaid,
      currencyId,
      mappedDealId,
      mappedTrancheId,
      customBondName,
      ...position
    }) => {
      const currencyItem = currencies.find(
          (currency) => (currency.id === currencyId) || (currency.id === defaultCurrencyId),
      );
      return ({
        ...position,
        datePurchased: position.datePurchased
            ? format(
              deprecatedParseLegacyDateString(position.datePurchased, TIMEZONE_DEFAULT),
              YEAR_MONTH_DAY_SLASH_FORMAT,
            ) : null,
        positionSize: positionSize ? positionSize.toString() : '',
        dollarPricePaid: dollarPricePaid ? dollarPricePaid.toString() : '',
        currencyId: currencyItem
            ? mapCurrencyToOption(currencyItem)
            : { value: currencyId, label: currencyId },
        bond: {
          dealId: mappedDealId,
          trancheId: mappedTrancheId,
          customBondName: customBondName || '',
        },
      });
    }),
  })) : [{
    id: uuid(),
    name: '',
    positions: [],
    priority: 1,
    isLocal: true,
  }],
});

export const isQueryStringHasValidLength = (query: string) => query.length >= 3 && query.length <= 128;
